import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Headphones,
  Plus,
  Trash2,
  Edit2,
  FileUp,
  Upload,
  Download,
  Search,
  Filter,
  Clock,
  Workflow,
  Bell,
  Shield,
  FileText,
  Calendar,
  SplitSquareVertical,
  Merge,
  CheckCircle2,
  XCircle,
  AlertTriangle,
} from "lucide-react";
import toast from "react-hot-toast";
import { usePermissions } from "../../rbac/PermissionsContext";

// Configuration (Admin) lives in localStorage for now (API-ready shape)
const STORAGE_KEY = "adminHelpdeskConfigV2";
const TICKETS_KEY = "helpdeskTicketsV2";

const defaultConfig = {
  version: 2,
  masters: {
    ticketTypes: [
      { id: "hr", name: "HR", enabled: true },
      { id: "it", name: "IT", enabled: true },
      { id: "admin", name: "Admin", enabled: true },
      { id: "finance", name: "Finance", enabled: true },
    ],
    categories: [
      {
        id: "it_support",
        typeId: "it",
        name: "IT Support",
        enabled: true,
        subcategories: [
          { id: "access", name: "Access / Permissions", enabled: true },
          { id: "device", name: "Device / Hardware", enabled: true },
        ],
      },
      {
        id: "hr_query",
        typeId: "hr",
        name: "HR Query",
        enabled: true,
        subcategories: [
          { id: "policy", name: "Policy clarification", enabled: true },
          { id: "leave", name: "Leave / Attendance", enabled: true },
        ],
      },
    ],
    priorities: [
      {
        id: "low",
        name: "Low",
        enabled: true,
        firstResponseMinutes: 240,
        resolutionMinutes: 2880,
        autoUpgradeAfterMinutes: null,
      },
      {
        id: "medium",
        name: "Medium",
        enabled: true,
        firstResponseMinutes: 120,
        resolutionMinutes: 1440,
        autoUpgradeAfterMinutes: null,
      },
      {
        id: "high",
        name: "High",
        enabled: true,
        firstResponseMinutes: 60,
        resolutionMinutes: 720,
        autoUpgradeAfterMinutes: null,
      },
      {
        id: "critical",
        name: "Critical",
        enabled: true,
        firstResponseMinutes: 15,
        resolutionMinutes: 240,
        autoUpgradeAfterMinutes: null,
      },
    ],
    statuses: [
      { id: "open", name: "Open", pauseSla: false, enabled: true },
      { id: "in_progress", name: "In Progress", pauseSla: false, enabled: true },
      { id: "on_hold", name: "On Hold", pauseSla: true, enabled: true, holdReasonMandatory: true },
      { id: "escalated", name: "Escalated", pauseSla: false, enabled: true },
      { id: "resolved", name: "Resolved", pauseSla: false, enabled: true },
      { id: "closed", name: "Closed", pauseSla: false, enabled: true },
      { id: "reopened", name: "Reopened", pauseSla: false, enabled: true },
    ],
  },
  sla: {
    timerStartsOn: "created_at",
    pause: {
      weeklyOff: true,
      companyHoliday: true,
      assigneeLeave: true,
      onHoldStatus: true,
      onHoldReasonMandatory: true,
    },
    businessHours: {
      workingDays: [1, 2, 3, 4, 5],
      shifts: [{ id: "general", name: "General", start: "09:30", end: "18:30" }],
    },
    holidays: { dates: [], behavior: "pause_sla" },
    escalationMatrix: [
      { level: 1, breach: "first_response", notifyRoles: ["helpdesk_l1"] },
      { level: 2, breach: "resolution", notifyRoles: ["helpdesk_l2"] },
      { level: 3, breach: "resolution", notifyRoles: ["helpdesk_l3"] },
    ],
  },
  assignment: {
    autoAssignmentEnabled: true,
    mode: "category_mapping", // category_mapping | dept_mapping | role_mapping | manual_queue
    mappings: [
      // { typeId, categoryId, departmentId?, routeTo: { kind: "role"|"user"|"department", key } }
      { typeId: "it", categoryId: "it_support", routeTo: { kind: "role", key: "it_support" } },
      { typeId: "hr", categoryId: "hr_query", routeTo: { kind: "role", key: "hr_admin" } },
    ],
    allowManualReassign: true,
    allowTransfer: { role: true, user: true, department: true },
  },
  workflow: {
    makerCheckerEnabled: false,
    approvalBasedTicketsEnabled: false,
    roleRules: [
      { role: "employee", canCreate: true, canComment: true, canClose: false },
      { role: "helpdesk_l1", canCreate: true, canComment: true, canClose: true },
    ],
    transitions: [
      { from: "open", to: ["in_progress", "on_hold", "escalated"] },
      { from: "in_progress", to: ["on_hold", "escalated", "resolved"] },
      { from: "on_hold", to: ["in_progress", "escalated"] },
      { from: "resolved", to: ["closed", "reopened"] },
      { from: "reopened", to: ["in_progress", "on_hold", "escalated"] },
    ],
  },
  notifications: {
    emailEnabled: false,
    systemEnabled: true,
    triggers: {
      created: true,
      assigned: true,
      reassigned: true,
      breached: true,
      escalated: true,
      resolved: true,
      closed: true,
      reopened: true,
    },
  },
  rca: {
    mandatoryForClosure: true,
    minChars: 50,
    categories: ["Process Gap", "System Issue", "Human Error", "External Dependency"],
    templates: [
      {
        id: "default",
        name: "Default RCA",
        enabled: true,
        sections: ["What happened?", "Why happened?", "Immediate fix", "Preventive action"],
      },
    ],
    closureCompliance: {
      requireResolutionNotes: true,
      requireRca: true,
      requireSupportingDocs: false,
    },
  },
  advanced: {
    allowMerge: true,
    allowSplit: true,
    knowledgeBaseEnabled: false,
    bulkUploadExcelEnabled: false,
    externalApiEnabled: false,
    auditTrailEnabled: true,
    dataRetentionDays: 365,
    prioritizationRulesEnabled: true,
  },
};

function safeParseJSON(raw, fallback) {
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function loadConfig() {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return defaultConfig;
  const parsed = safeParseJSON(raw, null);
  if (!parsed || typeof parsed !== "object") return defaultConfig;
  return { ...defaultConfig, ...parsed };
}

function saveConfig(cfg) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg));
  } catch {
    /* ignore */
  }
}

function loadTickets() {
  const raw = localStorage.getItem(TICKETS_KEY);
  const parsed = safeParseJSON(raw, []);
  return Array.isArray(parsed) ? parsed : [];
}

function saveTickets(rows) {
  try {
    localStorage.setItem(TICKETS_KEY, JSON.stringify(rows));
  } catch {
    /* ignore */
  }
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function dayKey(d = new Date()) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function nextTicketNumber(existing) {
  const today = dayKey();
  const prefix = `HD-${today.replaceAll("-", "")}-`;
  const nums = existing
    .map((t) => String(t.ticketNo || ""))
    .filter((x) => x.startsWith(prefix))
    .map((x) => Number(x.slice(prefix.length)))
    .filter((n) => Number.isFinite(n));
  const next = (nums.length ? Math.max(...nums) : 0) + 1;
  return `${prefix}${String(next).padStart(4, "0")}`;
}

function badge(text, tone = "slate") {
  const toneMap = {
    slate: "bg-gray-100 text-gray-700 border-gray-300",
    blue: "rh-accent-soft border-redhalo-teal/80",
    green: "border border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-300",
    amber: "bg-amber-100 text-amber-900 border-amber-300",
    red: "bg-rose-100 text-rose-800 border-rose-300",
    purple:
      "border border-redhalo-navy/30 bg-redhalo-mint/40 text-redhalo-navy dark:border-redhalo-navy/50 dark:bg-redhalo-navy/35 dark:text-redhalo-mint",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold border ${toneMap[tone] || toneMap.slate}`}
    >
      {text}
    </span>
  );
}

function SectionCard({ title, icon: Icon, children, right }) {
  return (
    <div className="border border-gray-300 bg-white dark:border-[#3C3C4C] dark:bg-[#1D1D1D]">
      <div className="flex items-center justify-between border-b border-gray-300 bg-gray-50 px-4 py-3 dark:border-[#3C3C4C] dark:bg-[#2a2a2a]">
        <div className="flex items-center gap-2">
          {Icon ? <Icon className="h-4 w-4 text-gray-600 dark:text-gray-400" /> : null}
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-800 dark:text-gray-100">{title}</h3>
        </div>
        {right}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function ModalShell({ open, title, onClose, children, wide }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[140] bg-black/50 flex items-center justify-center p-4">
      <div className={`bg-white border border-gray-300 shadow-xl w-full ${wide ? "max-w-4xl" : "max-w-xl"} max-h-[90vh] overflow-hidden flex flex-col`}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-300 bg-gray-50">
          <div className="text-sm font-semibold text-gray-900">{title}</div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 p-1 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-redhalo-red/40"
          >
            <XCircle className="h-4 w-4 text-gray-700" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

export default function HelpdeskConfigModal({ variant = "page" }) {
  const { hasModuleAccess, hasScreenAccess, isAdmin } = usePermissions();
  // Payroll-like framework: left sub-sidebar + main view
  const [activeTab, setActiveTab] = useState("dashboard");
  const [config, setConfig] = useState(() => loadConfig());
  const configRef = useRef(config);
  const [tickets, setTickets] = useState(() => loadTickets());
  const [savedFlash, setSavedFlash] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [ticketDraft, setTicketDraft] = useState({
    title: "",
    description: "",
    typeId: "it",
    categoryId: "it_support",
    subcategoryId: "access",
    priorityId: "medium",
    attachments: [],
  });
  const fileInputRef = useRef(null);

  useEffect(() => saveConfig(config), [config]);
  useEffect(() => saveTickets(tickets), [tickets]);

  const counts = useMemo(() => {
    const byStatus = new Map();
    for (const t of tickets) byStatus.set(t.status, (byStatus.get(t.status) || 0) + 1);
    return {
      open: (byStatus.get("open") || 0) + (byStatus.get("in_progress") || 0) + (byStatus.get("on_hold") || 0) + (byStatus.get("escalated") || 0) + (byStatus.get("reopened") || 0),
      closed: (byStatus.get("closed") || 0) + (byStatus.get("resolved") || 0),
      breached: tickets.filter((t) => t.sla?.breached).length,
      total: tickets.length,
    };
  }, [tickets]);

  const filteredTickets = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return tickets;
    return tickets.filter((t) => {
      return (
        String(t.ticketNo || "").toLowerCase().includes(q) ||
        String(t.title || "").toLowerCase().includes(q) ||
        String(t.description || "").toLowerCase().includes(q)
      );
    });
  }, [tickets, searchTerm]);

  const tabs = useMemo(
    () => [
      { id: "dashboard", label: "Dashboard", icon: Headphones },
      { id: "tickets", label: "Tickets", icon: FileText },
      { id: "masters", label: "Masters", icon: Workflow },
      { id: "sla", label: "SLA Policies", icon: Clock },
      { id: "workflow", label: "Workflow & RBAC", icon: Shield },
      { id: "notifications", label: "Notifications", icon: Bell },
      { id: "rca", label: "RCA & Closure", icon: FileText },
      { id: "reports", label: "Reports", icon: Filter },
      { id: "advanced", label: "Advanced", icon: AlertTriangle },
    ],
    []
  );

  const enabledTypes = config.masters.ticketTypes.filter((t) => t.enabled);
  const enabledCategories = config.masters.categories.filter((c) => c.enabled);
  const enabledPriorities = config.masters.priorities.filter((p) => p.enabled);
  const enabledStatuses = config.masters.statuses.filter((s) => s.enabled);
  const canViewHelpdesk =
    isAdmin ||
    hasModuleAccess("helpdesk", "view") ||
    hasScreenAccess("helpdesk", "tickets", "view") ||
    hasScreenAccess("helpdesk", "helpdesk_config", "view");
  const canViewTickets =
    isAdmin || hasScreenAccess("helpdesk", "tickets", "view");
  const canViewHelpdeskConfig =
    isAdmin || hasScreenAccess("helpdesk", "helpdesk_config", "view");
  const canCreateTickets =
    isAdmin ||
    hasModuleAccess("helpdesk", "create") ||
    hasScreenAccess("helpdesk", "tickets", "create");
  const canEditTickets =
    isAdmin ||
    hasModuleAccess("helpdesk", "edit") ||
    hasScreenAccess("helpdesk", "tickets", "edit");
  const canDeleteTickets =
    isAdmin ||
    hasModuleAccess("helpdesk", "delete") ||
    hasScreenAccess("helpdesk", "tickets", "delete");
  const canEditHelpdeskConfig =
    isAdmin ||
    hasModuleAccess("helpdesk", "edit") ||
    hasScreenAccess("helpdesk", "helpdesk_config", "edit");

  const safeSetConfig = useCallback(
    (updater) => {
      if (!canEditHelpdeskConfig) {
        toast.error("You have view-only access to Helpdesk configuration.");
        return;
      }
      setConfig(updater);
    },
    [canEditHelpdeskConfig]
  );

  const configurationTabs = useMemo(
    () => [
      "masters",
      "sla",
      "workflow",
      "notifications",
      "rca",
      "reports",
      "advanced",
    ],
    []
  );
  const visibleTabs = useMemo(
    () =>
      tabs.filter((t) => {
        if (!canViewHelpdesk) return false;
        if (t.id === "tickets") return canViewTickets;
        if (t.id === "dashboard")
          return canViewTickets || canViewHelpdeskConfig;
        return canViewHelpdeskConfig;
      }),
    [tabs, canViewHelpdesk, canViewTickets, canViewHelpdeskConfig]
  );
  useEffect(() => {
    if (!visibleTabs.some((t) => t.id === activeTab)) {
      setActiveTab(visibleTabs[0]?.id || "dashboard");
    }
  }, [activeTab, visibleTabs]);
  const isReadOnlyConfigTab =
    configurationTabs.includes(activeTab) && !canEditHelpdeskConfig;

  useEffect(() => {
    // Defense-in-depth: if a view-only user somehow triggers any config mutation
    // handler (DOM/script bypass), immediately roll back to the last authorized
    // snapshot so permissions are still enforced at runtime.
    if (canEditHelpdeskConfig) {
      configRef.current = config;
      return;
    }
    const prev = JSON.stringify(configRef.current);
    const curr = JSON.stringify(config);
    if (prev !== curr) {
      setConfig(configRef.current);
      toast.error("You have view-only access to Helpdesk configuration.");
    }
  }, [canEditHelpdeskConfig, config]);

  const createTicket = () => {
    if (!canCreateTickets) {
      toast.error("You do not have permission to create tickets.");
      return;
    }
    if (!ticketDraft.title.trim()) {
      toast.error("Ticket title is required.");
      return;
    }
    const now = new Date().toISOString();
    const ticketNo = nextTicketNumber(tickets);
    const row = {
      id: Date.now(),
      ticketNo,
      title: ticketDraft.title.trim(),
      description: ticketDraft.description.trim(),
      typeId: ticketDraft.typeId,
      categoryId: ticketDraft.categoryId,
      subcategoryId: ticketDraft.subcategoryId,
      priorityId: ticketDraft.priorityId,
      status: "open",
      createdAt: now,
      updatedAt: now,
      comments: [],
      attachments: ticketDraft.attachments || [],
      assignment: { mode: config.assignment.mode, autoAssigned: true, assignedTo: null },
      sla: { breached: false, firstResponseDue: null, resolutionDue: null },
      audit: [{ at: now, action: "created", by: "system" }],
    };
    setTickets((prev) => [row, ...prev]);
    setShowCreateTicket(false);
    setTicketDraft({
      title: "",
      description: "",
      typeId: enabledTypes[0]?.id || "it",
      categoryId: enabledCategories[0]?.id || "",
      subcategoryId: enabledCategories[0]?.subcategories?.[0]?.id || "",
      priorityId: enabledPriorities[0]?.id || "medium",
      attachments: [],
    });
  };

  const saveFlash = useCallback(() => {
    setSavedFlash(true);
    window.setTimeout(() => setSavedFlash(false), 1500);
  }, []);

  const guardConfigEdit = useCallback(() => {
    if (canEditHelpdeskConfig) return true;
    toast.error("You have view-only access to Helpdesk configuration.");
    return false;
  }, [canEditHelpdeskConfig]);

  return (
    <div className={variant === "page" ? "h-full w-full font-sans antialiased" : "font-sans antialiased"}>
      <div className="flex h-full min-h-0 w-full flex-1 overflow-hidden">
        {/* Sub Sidebar — same shell as Employee.jsx (sticky aside + Data tools) */}
        <aside
          className="sticky top-0 z-20 flex h-full min-h-0 w-56 shrink-0 flex-col overflow-hidden border-r border-[#165b5d]/30 bg-white shadow-sm dark:border-[#3C3C4C] dark:bg-[#121212]"
        >
          <div className="rh-subnav-aside-head border-b border-[#165b5d]/30 dark:border-[#3C3C4C]">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
              Helpdesk
            </h4>
          </div>
          <div className="min-h-0 flex-1 space-y-1 overflow-y-auto p-2">
            {visibleTabs.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  type="button"
                  className={`flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm transition-colors ${
                    activeTab === t.id
                      ? "rh-subnav-active"
                      : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800/80"
                  }`}
                  onClick={() => setActiveTab(t.id)}
                >
                  <Icon className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                  <span>{t.label}</span>
                </button>
              );
            })}
          </div>

          <div className="shrink-0 border-t border-[#165b5d]/30 p-2 pt-3 dark:border-[#3C3C4C]">
            <div className="rounded-2xl bg-[#165b5d]/30 p-4 shadow-inner ring-1 ring-[#165b5d]/25 dark:ring-[#165b5d]/40">
              <p className="text-[10px] font-bold uppercase tracking-wide text-[#134e50] dark:text-[#b8dcd9]">
                Data tools
              </p>
              <p className="mt-1 text-xs leading-snug text-slate-600 dark:text-slate-400">
                Import, export, or download helpdesk configuration and ticket data.
              </p>
              <div className="mt-3 space-y-1">
                <button
                  type="button"
                  disabled={!canEditHelpdeskConfig}
                  className={`flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm font-medium text-[#134e50] transition-colors hover:bg-[#165b5d]/35 dark:text-[#E0E0E0] dark:hover:bg-white/10 ${!canEditHelpdeskConfig ? "cursor-not-allowed opacity-45" : ""}`}
                  onClick={() => {
                    if (!guardConfigEdit()) return;
                    toast("Import helpdesk configuration when your backend or admin import flow is connected.", {
                      duration: 4000,
                    });
                  }}
                >
                  <FileUp className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                  Import
                </button>
                <button
                  type="button"
                  disabled={!canEditTickets && !canEditHelpdeskConfig}
                  className={`flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm font-medium text-[#134e50] transition-colors hover:bg-[#165b5d]/35 dark:text-[#E0E0E0] dark:hover:bg-white/10 ${!canEditTickets && !canEditHelpdeskConfig ? "cursor-not-allowed opacity-45" : ""}`}
                  onClick={() =>
                    toast("Export tickets or reports from the Tickets and Reports tabs when export is enabled.", {
                      duration: 4000,
                    })
                  }
                >
                  <Upload className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                  Export
                </button>
                <button
                  type="button"
                  disabled={!canViewHelpdesk}
                  className={`flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm font-medium text-[#134e50] transition-colors hover:bg-[#165b5d]/35 dark:text-[#E0E0E0] dark:hover:bg-white/10 ${!canViewHelpdesk ? "cursor-not-allowed opacity-45" : ""}`}
                  onClick={() =>
                    toast("Download sample templates from your administrator or use Masters to define categories.", {
                      duration: 4000,
                    })
                  }
                >
                  <Download className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                  Download
                </button>
              </div>
            </div>
          </div>
        </aside>

        {/* Tab content — flex sibling; aside width reserved in layout */}
        <div
          className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-4 text-gray-900 dark:text-gray-100"
          style={isReadOnlyConfigTab ? { pointerEvents: "none" } : undefined}
        >
          {isReadOnlyConfigTab && (
            <div className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              View-only access: Helpdesk configuration changes are disabled for your role.
            </div>
          )}
          {!canViewHelpdesk && (
            <div className="flex h-full items-center justify-center">
              <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                You do not have access to Helpdesk for this role.
              </div>
            </div>
          )}
          {/* Dashboard */}
          {activeTab === "dashboard" && canViewHelpdesk && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Helpdesk Dashboard</h1>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Real-time overview (local demo). API-ready for Node.js backend integration.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (!canCreateTickets) {
                      toast.error("You do not have permission to create tickets.");
                      return;
                    }
                    setShowCreateTicket(true);
                  }}
                  disabled={!canCreateTickets}
                  className={`flex items-center gap-2 rh-btn-primary px-4 py-2 hover:opacity-95 ${!canCreateTickets ? "cursor-not-allowed opacity-50" : ""}`}
                >
                  <Plus className="w-4 h-4" />
                  Create Ticket
                </button>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                <div className="border border-gray-300 bg-white p-4 dark:border-[#3C3C4C] dark:bg-[#1D1D1D]">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Open</div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{counts.open}</div>
                </div>
                <div className="border border-gray-300 bg-white p-4 dark:border-[#3C3C4C] dark:bg-[#1D1D1D]">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Closed/Resolved</div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{counts.closed}</div>
                </div>
                <div className="border border-gray-300 bg-white p-4 dark:border-[#3C3C4C] dark:bg-[#1D1D1D]">
                  <div className="text-xs text-gray-500 dark:text-gray-400">SLA Breaches</div>
                  <div className="text-2xl font-bold text-rose-700 dark:text-rose-400">{counts.breached}</div>
                </div>
                <div className="border border-gray-300 bg-white p-4 dark:border-[#3C3C4C] dark:bg-[#1D1D1D]">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Total</div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{counts.total}</div>
                </div>
              </div>

              <SectionCard
                title="Quick insights"
                icon={AlertTriangle}
                right={<div className="flex items-center gap-2">{badge("SLA compliance %", "blue")}{badge("RCA trend", "purple")}</div>}
              >
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  <div className="border border-gray-200 bg-gray-50 p-3 dark:border-[#3C3C4C] dark:bg-[#121212]">
                    <div className="text-xs text-gray-500 dark:text-gray-400">Average resolution time</div>
                    <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">—</div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-500">Shown when backend computes durations</div>
                  </div>
                  <div className="border border-gray-200 bg-gray-50 p-3 dark:border-[#3C3C4C] dark:bg-[#121212]">
                    <div className="text-xs text-gray-500 dark:text-gray-400">Tickets by category</div>
                    <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">—</div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-500">Pivot-ready report block</div>
                  </div>
                  <div className="border border-gray-200 bg-gray-50 p-3 dark:border-[#3C3C4C] dark:bg-[#121212]">
                    <div className="text-xs text-gray-500 dark:text-gray-400">Role-wise workload</div>
                    <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">—</div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-500">Use assignment + audit trail</div>
                  </div>
                </div>
              </SectionCard>
            </div>
          )}

          {/* Tickets */}
          {activeTab === "tickets" && canViewTickets && (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Tickets</h1>
                  <p className="text-sm text-gray-600 mt-1">
                    Create, search, view, merge/split (toggles in Advanced), reassignment (planned).
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (!canCreateTickets) {
                        toast.error("You do not have permission to create tickets.");
                        return;
                      }
                      setShowCreateTicket(true);
                    }}
                    disabled={!canCreateTickets}
                    className={`flex items-center gap-2 rh-btn-primary px-4 py-2 hover:opacity-95 ${!canCreateTickets ? "cursor-not-allowed opacity-50" : ""}`}
                  >
                    <Plus className="w-4 h-4" />
                    New Ticket
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div className="relative min-w-[240px] flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                  <input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by ticket no / title / description"
                    className="w-full border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-redhalo-red focus:outline-none focus:ring-1 focus:ring-redhalo-red/35 dark:border-[#3C3C4C] dark:bg-[#121212] dark:text-gray-100"
                  />
                </div>
                <button
                  type="button"
                  className="flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-800 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-redhalo-red/35 dark:border-[#3C3C4C] dark:text-gray-100 dark:hover:bg-[#2a2a2a]"
                >
                  <Filter className="h-4 w-4" /> Filters
                </button>
                <button
                  type="button"
                  className="flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-800 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-redhalo-red/35 dark:border-[#3C3C4C] dark:text-gray-100 dark:hover:bg-[#2a2a2a]"
                >
                  <Download className="h-4 w-4" /> Export
                </button>
              </div>

              <div className="overflow-hidden border border-gray-300 bg-white dark:border-[#3C3C4C] dark:bg-[#1D1D1D]">
                <div className="grid grid-cols-12 gap-2 border-b border-gray-300 bg-gray-50 px-3 py-2 text-[11px] font-semibold uppercase text-gray-600 dark:border-[#3C3C4C] dark:bg-[#2a2a2a] dark:text-gray-300">
                  <div className="col-span-2">Ticket</div>
                  <div className="col-span-4">Title</div>
                  <div className="col-span-2">Type / Category</div>
                  <div className="col-span-1">Priority</div>
                  <div className="col-span-1">Status</div>
                  <div className="col-span-2 text-right">Actions</div>
                </div>
                <div className="max-h-[calc(100dvh-20rem)] overflow-y-auto">
                  {filteredTickets.length === 0 ? (
                    <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">No tickets found.</div>
                  ) : (
                    filteredTickets.map((t) => {
                      const typeName = config.masters.ticketTypes.find((x) => x.id === t.typeId)?.name || t.typeId;
                      const catName = config.masters.categories.find((x) => x.id === t.categoryId)?.name || t.categoryId;
                      const pr = config.masters.priorities.find((x) => x.id === t.priorityId)?.name || t.priorityId;
                      return (
                        <div
                          key={t.id}
                          className="grid grid-cols-12 items-center gap-2 border-b border-gray-200 px-3 py-2 text-sm dark:border-[#3C3C4C]"
                        >
                          <div className="col-span-2">
                            <div className="font-semibold text-gray-900 dark:text-gray-100">{t.ticketNo}</div>
                            <div className="text-[11px] text-gray-500 dark:text-gray-400">{new Date(t.createdAt).toLocaleString()}</div>
                          </div>
                          <div className="col-span-4 min-w-0">
                            <div className="truncate font-medium text-gray-900 dark:text-gray-100">{t.title}</div>
                            <div className="truncate text-[11px] text-gray-600 dark:text-gray-400">{t.description || "—"}</div>
                          </div>
                          <div className="col-span-2">
                            <div className="text-xs dark:text-gray-300">{typeName}</div>
                            <div className="text-[11px] text-gray-500 dark:text-gray-400">{catName}</div>
                          </div>
                          <div className="col-span-1">
                            {badge(pr, pr === "Critical" ? "red" : pr === "High" ? "amber" : "slate")}
                          </div>
                          <div className="col-span-1">{badge(t.status, t.status === "closed" ? "green" : "blue")}</div>
                          <div className="col-span-2 flex justify-end gap-1">
                            {config.advanced.allowMerge && (
                              <button
                                type="button"
                                className="border border-gray-300 p-2 hover:bg-gray-50 dark:border-[#3C3C4C] dark:hover:bg-[#2a2a2a]"
                                title="Merge"
                              >
                                <Merge className="h-4 w-4" />
                              </button>
                            )}
                            {config.advanced.allowSplit && (
                              <button
                                type="button"
                                className="border border-gray-300 p-2 hover:bg-gray-50 dark:border-[#3C3C4C] dark:hover:bg-[#2a2a2a]"
                                title="Split"
                              >
                                <SplitSquareVertical className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              type="button"
                              disabled={!canEditTickets}
                              className={`border border-gray-300 p-2 hover:bg-gray-50 dark:border-[#3C3C4C] dark:hover:bg-[#2a2a2a] ${!canEditTickets ? "cursor-not-allowed opacity-45" : ""}`}
                              title="Edit (planned)"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              disabled={!canDeleteTickets}
                              className={`border border-gray-300 p-2 text-rose-700 hover:bg-gray-50 dark:border-[#3C3C4C] dark:text-rose-400 dark:hover:bg-[#2a2a2a] ${!canDeleteTickets ? "cursor-not-allowed opacity-45" : ""}`}
                              title="Delete"
                              onClick={() => {
                                if (!canDeleteTickets) {
                                  toast.error("You do not have permission to delete tickets.");
                                  return;
                                }
                                if (!window.confirm("Delete this ticket?")) return;
                                setTickets((prev) => prev.filter((x) => x.id !== t.id));
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Masters */}
          {activeTab === "masters" && canViewHelpdeskConfig && (
            <div className="space-y-4">
              <h1 className="text-2xl font-bold text-gray-900">Master configuration</h1>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <SectionCard
                  title="Ticket Types"
                  icon={Workflow}
                  right={
                    <button
                      type="button"
                      className="px-3 py-1.5 text-sm rh-btn-primary hover:opacity-95 flex items-center gap-2"
                      onClick={() => {
                        const name = window.prompt("Ticket type name");
                        if (!name) return;
                        const id = name.toLowerCase().replace(/[^a-z0-9]+/g, "_").slice(0, 32) || `${Date.now()}`;
                        safeSetConfig((c) => ({
                          ...c,
                          masters: {
                            ...c.masters,
                            ticketTypes: [...c.masters.ticketTypes, { id, name, enabled: true }],
                          },
                        }));
                        saveFlash();
                      }}
                    >
                      <Plus className="w-4 h-4" /> Add
                    </button>
                  }
                >
                  <div className="space-y-2">
                    {config.masters.ticketTypes.map((t) => (
                      <div key={t.id} className="flex items-center justify-between border border-gray-200 px-3 py-2">
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900 truncate">{t.name}</div>
                          <div className="text-[11px] text-gray-500">Key: {t.id}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-gray-600 inline-flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={t.enabled}
                              onChange={(e) =>
                                safeSetConfig((c) => ({
                                  ...c,
                                  masters: {
                                    ...c.masters,
                                    ticketTypes: c.masters.ticketTypes.map((x) =>
                                      x.id === t.id ? { ...x, enabled: e.target.checked } : x
                                    ),
                                  },
                                }))
                              }
                            />
                            Enabled
                          </label>
                          <button
                            type="button"
                            className="p-2 border border-gray-300 hover:bg-gray-50 text-rose-700"
                            onClick={() => {
                              if (!window.confirm("Delete ticket type?")) return;
                              safeSetConfig((c) => ({
                                ...c,
                                masters: {
                                  ...c.masters,
                                  ticketTypes: c.masters.ticketTypes.filter((x) => x.id !== t.id),
                                  categories: c.masters.categories.filter((x) => x.typeId !== t.id),
                                },
                              }));
                              saveFlash();
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </SectionCard>

                <SectionCard title="Priorities (with timelines)" icon={Clock}>
                  <div className="space-y-3">
                    {config.masters.priorities.map((p) => (
                      <div key={p.id} className="border border-gray-200 p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="font-semibold text-gray-900">{p.name}</div>
                          <label className="text-xs text-gray-600 inline-flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={p.enabled}
                              onChange={(e) =>
                                safeSetConfig((c) => ({
                                  ...c,
                                  masters: {
                                    ...c.masters,
                                    priorities: c.masters.priorities.map((x) =>
                                      x.id === p.id ? { ...x, enabled: e.target.checked } : x
                                    ),
                                  },
                                }))
                              }
                            />
                            Enabled
                          </label>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                          <div>
                            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-600">First response (min)</div>
                            <input
                              type="number"
                              value={p.firstResponseMinutes}
                              onChange={(e) =>
                                safeSetConfig((c) => ({
                                  ...c,
                                  masters: {
                                    ...c.masters,
                                    priorities: c.masters.priorities.map((x) =>
                                      x.id === p.id ? { ...x, firstResponseMinutes: Number(e.target.value) || 0 } : x
                                    ),
                                  },
                                }))
                              }
                              className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-redhalo-red focus:ring-2 focus:ring-redhalo-red/30"
                            />
                          </div>
                          <div>
                            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-600">Resolution (min)</div>
                            <input
                              type="number"
                              value={p.resolutionMinutes}
                              onChange={(e) =>
                                safeSetConfig((c) => ({
                                  ...c,
                                  masters: {
                                    ...c.masters,
                                    priorities: c.masters.priorities.map((x) =>
                                      x.id === p.id ? { ...x, resolutionMinutes: Number(e.target.value) || 0 } : x
                                    ),
                                  },
                                }))
                              }
                              className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-redhalo-red focus:ring-2 focus:ring-redhalo-red/30"
                            />
                          </div>
                          <div>
                            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-600">Auto-upgrade (min)</div>
                            <input
                              type="number"
                              value={p.autoUpgradeAfterMinutes ?? ""}
                              onChange={(e) => {
                                const v = e.target.value;
                                safeSetConfig((c) => ({
                                  ...c,
                                  masters: {
                                    ...c.masters,
                                    priorities: c.masters.priorities.map((x) =>
                                      x.id === p.id ? { ...x, autoUpgradeAfterMinutes: v === "" ? null : Number(v) || 0 } : x
                                    ),
                                  },
                                }));
                              }}
                              className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-redhalo-red focus:ring-2 focus:ring-redhalo-red/30"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </SectionCard>
              </div>

              <SectionCard
                title="Categories & Subcategories (multi-level)"
                icon={Workflow}
                right={
                  <button
                    type="button"
                    className="px-3 py-1.5 text-sm rh-btn-primary hover:opacity-95 flex items-center gap-2"
                    onClick={() => {
                      const name = window.prompt("Category name");
                      if (!name) return;
                      const typeId = enabledTypes[0]?.id || "it";
                      const id = name.toLowerCase().replace(/[^a-z0-9]+/g, "_").slice(0, 32) || `${Date.now()}`;
                      safeSetConfig((c) => ({
                        ...c,
                        masters: {
                          ...c.masters,
                          categories: [
                            ...c.masters.categories,
                            { id, typeId, name, enabled: true, subcategories: [] },
                          ],
                        },
                      }));
                      saveFlash();
                    }}
                  >
                    <Plus className="w-4 h-4" /> Add category
                  </button>
                }
              >
                <div className="space-y-3">
                  {config.masters.categories.map((cRow) => (
                    <div key={cRow.id} className="border border-gray-200">
                      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
                        <div className="min-w-0">
                          <div className="font-semibold text-gray-900 truncate">{cRow.name}</div>
                          <div className="text-[11px] text-gray-500">
                            Type: {config.masters.ticketTypes.find((t) => t.id === cRow.typeId)?.name || cRow.typeId}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            className="px-2 py-1 text-xs border border-gray-300 hover:bg-white flex items-center gap-1"
                            onClick={() => {
                              const nm = window.prompt("Subcategory name");
                              if (!nm) return;
                              const id = nm.toLowerCase().replace(/[^a-z0-9]+/g, "_").slice(0, 32) || `${Date.now()}`;
                              safeSetConfig((cfg) => ({
                                ...cfg,
                                masters: {
                                  ...cfg.masters,
                                  categories: cfg.masters.categories.map((x) =>
                                    x.id === cRow.id
                                      ? { ...x, subcategories: [...(x.subcategories || []), { id, name: nm, enabled: true }] }
                                      : x
                                  ),
                                },
                              }));
                              saveFlash();
                            }}
                          >
                            <Plus className="w-3.5 h-3.5" /> Subcategory
                          </button>
                          <button
                            className="p-2 border border-gray-300 hover:bg-white text-rose-700"
                            onClick={() => {
                              if (!window.confirm("Delete category?")) return;
                              safeSetConfig((cfg) => ({
                                ...cfg,
                                masters: {
                                  ...cfg.masters,
                                  categories: cfg.masters.categories.filter((x) => x.id !== cRow.id),
                                },
                              }));
                              saveFlash();
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="p-3 flex flex-wrap gap-2">
                        {(cRow.subcategories || []).length === 0 ? (
                          <div className="text-sm text-gray-500">No subcategories.</div>
                        ) : (
                          cRow.subcategories.map((s) => (
                            <div key={s.id} className="inline-flex items-center gap-2 border border-gray-200 bg-gray-50 px-3 py-1">
                              <span className="text-xs text-gray-800">{s.name}</span>
                              <button
                                className="text-rose-700"
                                onClick={() => {
                                  if (!window.confirm("Delete subcategory?")) return;
                                  safeSetConfig((cfg) => ({
                                    ...cfg,
                                    masters: {
                                      ...cfg.masters,
                                      categories: cfg.masters.categories.map((x) =>
                                        x.id === cRow.id
                                          ? { ...x, subcategories: (x.subcategories || []).filter((z) => z.id !== s.id) }
                                          : x
                                      ),
                                    },
                                  }));
                                  saveFlash();
                                }}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </SectionCard>

              <SectionCard title="Status workflow (lifecycle)" icon={Workflow}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {config.masters.statuses.map((s) => (
                    <div key={s.id} className="border border-gray-200 px-3 py-2 flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900">{s.name}</div>
                        <div className="text-[11px] text-gray-500">
                          Key: {s.id} {s.pauseSla ? "• SLA pause" : ""}
                        </div>
                      </div>
                      <label className="text-xs text-gray-600 inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={s.enabled}
                          onChange={(e) =>
                            safeSetConfig((cfg) => ({
                              ...cfg,
                              masters: {
                                ...cfg.masters,
                                statuses: cfg.masters.statuses.map((x) =>
                                  x.id === s.id ? { ...x, enabled: e.target.checked } : x
                                ),
                              },
                            }))
                          }
                        />
                        Enabled
                      </label>
                    </div>
                  ))}
                </div>
              </SectionCard>
            </div>
          )}

          {/* SLA */}
          {activeTab === "sla" && canViewHelpdeskConfig && (
            <div className="space-y-4">
              <h1 className="text-2xl font-bold text-gray-900">SLA Policies</h1>
              <SectionCard title="Escalation Matrix (L1/L2/L3)" icon={Workflow}>
                <div className="space-y-2">
                  {config.sla.escalationMatrix.map((row, idx) => (
                    <div key={`${row.level}-${idx}`} className="border border-gray-200 p-3 space-y-2">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2">
                          {badge(`Level ${row.level}`, "blue")}
                          {badge(row.breach === "first_response" ? "First response breach" : "Resolution breach", row.breach === "first_response" ? "amber" : "red")}
                        </div>
                        <button
                          type="button"
                          className="px-2 py-1 text-xs border border-gray-300 hover:bg-gray-50 text-rose-700 flex items-center gap-1"
                          onClick={() =>
                            safeSetConfig((cfg) => ({
                              ...cfg,
                              sla: {
                                ...cfg.sla,
                                escalationMatrix: cfg.sla.escalationMatrix.filter((_, i) => i !== idx),
                              },
                            }))
                          }
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Remove
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div>
                          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-600">Breach type</div>
                          <select
                            value={row.breach}
                            onChange={(e) => {
                              const breach = e.target.value;
                              safeSetConfig((cfg) => {
                                const next = [...cfg.sla.escalationMatrix];
                                next[idx] = { ...next[idx], breach };
                                return { ...cfg, sla: { ...cfg.sla, escalationMatrix: next } };
                              });
                            }}
                            className="w-full border border-gray-300 px-2 py-1.5 text-sm bg-white"
                          >
                            <option value="first_response">First response</option>
                            <option value="resolution">Resolution</option>
                          </select>
                        </div>
                        <div>
                          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-600">Notify roles (comma separated)</div>
                          <input
                            value={(row.notifyRoles || []).join(",")}
                            onChange={(e) => {
                              const notifyRoles = e.target.value.split(",").map((x) => x.trim()).filter(Boolean);
                              safeSetConfig((cfg) => {
                                const next = [...cfg.sla.escalationMatrix];
                                next[idx] = { ...next[idx], notifyRoles };
                                return { ...cfg, sla: { ...cfg.sla, escalationMatrix: next } };
                              });
                            }}
                            className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-redhalo-red focus:ring-2 focus:ring-redhalo-red/30"
                            placeholder="helpdesk_l2, it_admin"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="px-3 py-2 text-sm rh-btn-primary hover:opacity-95 inline-flex items-center gap-2"
                    onClick={() =>
                      safeSetConfig((cfg) => ({
                        ...cfg,
                        sla: {
                          ...cfg.sla,
                          escalationMatrix: [
                            ...cfg.sla.escalationMatrix,
                            { level: (cfg.sla.escalationMatrix.at(-1)?.level || 0) + 1, breach: "resolution", notifyRoles: [] },
                          ],
                        },
                      }))
                    }
                  >
                    <Plus className="w-4 h-4" /> Add level
                  </button>
                </div>
              </SectionCard>

              <SectionCard title="SLA pause conditions" icon={Clock}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {Object.entries(config.sla.pause).map(([k, v]) => (
                    <label key={k} className="inline-flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={!!v}
                        onChange={(e) =>
                          safeSetConfig((cfg) => ({
                            ...cfg,
                            sla: { ...cfg.sla, pause: { ...cfg.sla.pause, [k]: e.target.checked } },
                          }))
                        }
                      />
                      {k}
                    </label>
                  ))}
                </div>
              </SectionCard>

              <SectionCard title="Business hours (working days & shifts)" icon={Calendar}>
                <div className="text-sm text-gray-700">
                  Shifts are used for SLA computation in backend. Current UI captures shift start/end.
                </div>
                <div className="mt-3 space-y-2">
                  {config.sla.businessHours.shifts.map((s, idx) => (
                    <div key={s.id} className="border border-gray-200 p-3 grid grid-cols-1 md:grid-cols-3 gap-2">
                      <input
                        value={s.name}
                        onChange={(e) => {
                          const name = e.target.value;
                          safeSetConfig((cfg) => {
                            const next = [...cfg.sla.businessHours.shifts];
                            next[idx] = { ...next[idx], name };
                            return { ...cfg, sla: { ...cfg.sla, businessHours: { ...cfg.sla.businessHours, shifts: next } } };
                          });
                        }}
                        className="border border-gray-300 px-2 py-1.5 text-sm"
                        placeholder="Shift name"
                      />
                      <input
                        value={s.start}
                        onChange={(e) => {
                          const start = e.target.value;
                          safeSetConfig((cfg) => {
                            const next = [...cfg.sla.businessHours.shifts];
                            next[idx] = { ...next[idx], start };
                            return { ...cfg, sla: { ...cfg.sla, businessHours: { ...cfg.sla.businessHours, shifts: next } } };
                          });
                        }}
                        className="border border-gray-300 px-2 py-1.5 text-sm"
                        placeholder="Start (HH:MM)"
                      />
                      <input
                        value={s.end}
                        onChange={(e) => {
                          const end = e.target.value;
                          safeSetConfig((cfg) => {
                            const next = [...cfg.sla.businessHours.shifts];
                            next[idx] = { ...next[idx], end };
                            return { ...cfg, sla: { ...cfg.sla, businessHours: { ...cfg.sla.businessHours, shifts: next } } };
                          });
                        }}
                        className="border border-gray-300 px-2 py-1.5 text-sm"
                        placeholder="End (HH:MM)"
                      />
                    </div>
                  ))}
                </div>
              </SectionCard>
            </div>
          )}

          {/* Workflow */}
          {activeTab === "workflow" && canViewHelpdeskConfig && (
            <div className="space-y-4">
              <h1 className="text-2xl font-bold text-gray-900">Workflow & Role-Based Access</h1>
              <SectionCard title="Maker-checker & approval based tickets" icon={Shield}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={!!config.workflow.makerCheckerEnabled}
                      onChange={(e) =>
                        safeSetConfig((cfg) => ({ ...cfg, workflow: { ...cfg.workflow, makerCheckerEnabled: e.target.checked } }))
                      }
                    />
                    Maker-checker enabled
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={!!config.workflow.approvalBasedTicketsEnabled}
                      onChange={(e) =>
                        safeSetConfig((cfg) => ({ ...cfg, workflow: { ...cfg.workflow, approvalBasedTicketsEnabled: e.target.checked } }))
                      }
                    />
                    Approval based tickets enabled
                  </label>
                </div>
              </SectionCard>

              <SectionCard title="Status transitions" icon={Workflow}>
                <div className="space-y-2">
                  {config.workflow.transitions.map((tr, idx) => (
                    <div key={idx} className="border border-gray-200 p-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div>
                        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-600">From</div>
                        <input
                          value={tr.from}
                          onChange={(e) => {
                            const from = e.target.value;
                            safeSetConfig((cfg) => {
                              const next = [...cfg.workflow.transitions];
                              next[idx] = { ...next[idx], from };
                              return { ...cfg, workflow: { ...cfg.workflow, transitions: next } };
                            });
                          }}
                          className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-redhalo-red focus:ring-2 focus:ring-redhalo-red/30"
                        />
                      </div>
                      <div>
                        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-600">To (comma separated)</div>
                        <input
                          value={(tr.to || []).join(",")}
                          onChange={(e) => {
                            const to = e.target.value.split(",").map((x) => x.trim()).filter(Boolean);
                            safeSetConfig((cfg) => {
                              const next = [...cfg.workflow.transitions];
                              next[idx] = { ...next[idx], to };
                              return { ...cfg, workflow: { ...cfg.workflow, transitions: next } };
                            });
                          }}
                          className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-redhalo-red focus:ring-2 focus:ring-redhalo-red/30"
                        />
                      </div>
                      <div className="md:col-span-2 flex justify-end">
                        <button
                          type="button"
                          className="px-2 py-1 text-xs border border-gray-300 hover:bg-gray-50 text-rose-700 flex items-center gap-1"
                          onClick={() =>
                            safeSetConfig((cfg) => ({
                              ...cfg,
                              workflow: { ...cfg.workflow, transitions: cfg.workflow.transitions.filter((_, i) => i !== idx) },
                            }))
                          }
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Remove
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="px-3 py-2 text-sm rh-btn-primary hover:opacity-95 inline-flex items-center gap-2"
                    onClick={() =>
                      safeSetConfig((cfg) => ({
                        ...cfg,
                        workflow: { ...cfg.workflow, transitions: [...cfg.workflow.transitions, { from: "open", to: ["in_progress"] }] },
                      }))
                    }
                  >
                    <Plus className="w-4 h-4" /> Add transition
                  </button>
                </div>
              </SectionCard>
            </div>
          )}

          {/* Notifications */}
          {activeTab === "notifications" && canViewHelpdeskConfig && (
            <div className="space-y-4">
              <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
              <SectionCard title="Channels" icon={Bell}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={!!config.notifications.emailEnabled}
                      onChange={(e) =>
                        safeSetConfig((cfg) => ({ ...cfg, notifications: { ...cfg.notifications, emailEnabled: e.target.checked } }))
                      }
                    />
                    Email notifications
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={!!config.notifications.systemEnabled}
                      onChange={(e) =>
                        safeSetConfig((cfg) => ({ ...cfg, notifications: { ...cfg.notifications, systemEnabled: e.target.checked } }))
                      }
                    />
                    System notifications
                  </label>
                </div>
              </SectionCard>
              <SectionCard title="Trigger configuration" icon={Bell}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {Object.entries(config.notifications.triggers).map(([k, v]) => (
                    <label key={k} className="inline-flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={!!v}
                        onChange={(e) =>
                          safeSetConfig((cfg) => ({
                            ...cfg,
                            notifications: {
                              ...cfg.notifications,
                              triggers: { ...cfg.notifications.triggers, [k]: e.target.checked },
                            },
                          }))
                        }
                      />
                      {k}
                    </label>
                  ))}
                </div>
              </SectionCard>
            </div>
          )}

          {/* RCA */}
          {activeTab === "rca" && canViewHelpdeskConfig && (
            <div className="space-y-4">
              <h1 className="text-2xl font-bold text-gray-900">RCA & Closure Compliance</h1>
              <SectionCard title="Mandatory fields before closure" icon={FileText}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={!!config.rca.mandatoryForClosure}
                      onChange={(e) => safeSetConfig((cfg) => ({ ...cfg, rca: { ...cfg.rca, mandatoryForClosure: e.target.checked } }))}
                    />
                    RCA mandatory
                  </label>
                  <div>
                    <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-600">Minimum RCA chars</div>
                    <input
                      type="number"
                      value={config.rca.minChars}
                      onChange={(e) => safeSetConfig((cfg) => ({ ...cfg, rca: { ...cfg.rca, minChars: Number(e.target.value) || 0 } }))}
                      className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-redhalo-red focus:ring-2 focus:ring-redhalo-red/30"
                    />
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
                  {Object.entries(config.rca.closureCompliance).map(([k, v]) => (
                    <label key={k} className="inline-flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={!!v}
                        onChange={(e) =>
                          safeSetConfig((cfg) => ({
                            ...cfg,
                            rca: { ...cfg.rca, closureCompliance: { ...cfg.rca.closureCompliance, [k]: e.target.checked } },
                          }))
                        }
                      />
                      {k}
                    </label>
                  ))}
                </div>
              </SectionCard>
              <SectionCard title="RCA categories" icon={FileText}>
                <textarea
                  value={config.rca.categories.join("\n")}
                  onChange={(e) =>
                    safeSetConfig((cfg) => ({ ...cfg, rca: { ...cfg.rca, categories: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) } }))
                  }
                  rows={6}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-redhalo-red focus:ring-2 focus:ring-redhalo-red/30"
                />
              </SectionCard>
            </div>
          )}

          {/* Reports */}
          {activeTab === "reports" && canViewHelpdeskConfig && (
            <div className="space-y-4">
              <h1 className="text-2xl font-bold text-gray-900">Dashboard & Reports</h1>
              <SectionCard title="Real-time dashboards (placeholders)" icon={CheckCircle2}>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div className="border border-gray-200 p-3 bg-gray-50">
                    <div className="text-xs text-gray-600">Open vs Closed</div>
                    <div className="text-lg font-bold">{counts.open} / {counts.closed}</div>
                  </div>
                  <div className="border border-gray-200 p-3 bg-gray-50">
                    <div className="text-xs text-gray-600">SLA compliance %</div>
                    <div className="text-lg font-bold">—</div>
                  </div>
                  <div className="border border-gray-200 p-3 bg-gray-50">
                    <div className="text-xs text-gray-600">Avg resolution time</div>
                    <div className="text-lg font-bold">—</div>
                  </div>
                  <div className="border border-gray-200 p-3 bg-gray-50">
                    <div className="text-xs text-gray-600">RCA trend</div>
                    <div className="text-lg font-bold">—</div>
                  </div>
                </div>
              </SectionCard>
              <SectionCard title="Advanced reports (API-ready)" icon={Download}>
                <div className="text-sm text-gray-700 space-y-2">
                  <div>- Employee-wise tickets</div>
                  <div>- Role-wise workload</div>
                  <div>- Escalation tracking</div>
                  <div>- RCA trend analysis</div>
                </div>
              </SectionCard>
            </div>
          )}

          {/* Advanced */}
          {activeTab === "advanced" && canViewHelpdeskConfig && (
            <div className="space-y-4">
              <h1 className="text-2xl font-bold text-gray-900">Advanced Features</h1>
              <SectionCard title="Automation, integrations, audit & retention" icon={AlertTriangle}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {Object.entries(config.advanced).map(([k, v]) => (
                    <label key={k} className="inline-flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type={typeof v === "boolean" ? "checkbox" : "text"}
                        checked={typeof v === "boolean" ? !!v : undefined}
                        value={typeof v === "boolean" ? undefined : v}
                        onChange={(e) => {
                          const nextVal =
                            typeof v === "boolean"
                              ? e.target.checked
                              : Number(e.target.value) || 0;
                          safeSetConfig((cfg) => ({ ...cfg, advanced: { ...cfg.advanced, [k]: nextVal } }));
                        }}
                      />
                      {k}
                    </label>
                  ))}
                </div>
                <div className="mt-3 text-xs text-gray-500">
                  Backend implementation suggestion: Node.js service with immutable audit log events for status changes, assignments, comments, SLA pause/resume, and merge/split operations.
                </div>
              </SectionCard>
              <SectionCard title="Bulk upload (Excel) – placeholder" icon={Upload}>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="rh-btn-secondary flex items-center gap-2 rounded-md px-3 py-2 text-sm hover:opacity-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-redhalo-red/40"
                  >
                    <Upload className="h-4 w-4" /> Upload Excel
                  </button>
                  <span className="text-xs text-gray-500">Enabled when backend parser is wired.</span>
                </div>
              </SectionCard>
            </div>
          )}

          {/* Save feedback */}
          <div className="mt-6 min-h-[1rem] text-xs text-emerald-700">
            {savedFlash ? "Saved." : ""}
          </div>
        </div>
      </div>

      {/* Create ticket modal */}
      <ModalShell
        open={showCreateTicket && canCreateTickets}
        title="Create helpdesk ticket"
        onClose={() => setShowCreateTicket(false)}
        wide
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-600">Title *</div>
            <input
              value={ticketDraft.title}
              onChange={(e) => setTicketDraft((d) => ({ ...d, title: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-redhalo-red focus:ring-2 focus:ring-redhalo-red/30"
              placeholder="Short summary"
            />
          </div>
          <div className="md:col-span-2">
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-600">Description</div>
            <textarea
              value={ticketDraft.description}
              onChange={(e) => setTicketDraft((d) => ({ ...d, description: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-redhalo-red focus:ring-2 focus:ring-redhalo-red/30"
              rows={4}
              placeholder="Details, impact, steps tried..."
            />
          </div>

          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-600">Ticket type</div>
            <select
              value={ticketDraft.typeId}
              onChange={(e) => setTicketDraft((d) => ({ ...d, typeId: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-redhalo-red focus:ring-2 focus:ring-redhalo-red/30"
            >
              {enabledTypes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-600">Priority</div>
            <select
              value={ticketDraft.priorityId}
              onChange={(e) => setTicketDraft((d) => ({ ...d, priorityId: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-redhalo-red focus:ring-2 focus:ring-redhalo-red/30"
            >
              {enabledPriorities.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-600">Category</div>
            <select
              value={ticketDraft.categoryId}
              onChange={(e) => {
                const categoryId = e.target.value;
                const cat = enabledCategories.find((c) => c.id === categoryId);
                setTicketDraft((d) => ({
                  ...d,
                  categoryId,
                  subcategoryId: cat?.subcategories?.[0]?.id || "",
                }));
              }}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-redhalo-red focus:ring-2 focus:ring-redhalo-red/30"
            >
              {enabledCategories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-600">Subcategory</div>
            <select
              value={ticketDraft.subcategoryId}
              onChange={(e) => setTicketDraft((d) => ({ ...d, subcategoryId: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-redhalo-red focus:ring-2 focus:ring-redhalo-red/30"
            >
              {(enabledCategories.find((c) => c.id === ticketDraft.categoryId)?.subcategories || []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-600">Attachments</div>
            <div className="flex items-center gap-2">
              <input ref={fileInputRef} type="file" multiple className="hidden" onChange={(e) => {
                const files = Array.from(e.target.files || []).map((f) => ({ name: f.name, size: f.size, type: f.type }));
                setTicketDraft((d) => ({ ...d, attachments: [...(d.attachments || []), ...files] }));
                e.target.value = "";
              }} />
              <button
                type="button"
                className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-redhalo-red/35 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
                onClick={() => {
                  if (!canCreateTickets) {
                    toast.error("You do not have permission to create tickets.");
                    return;
                  }
                  fileInputRef.current?.click();
                }}
              >
                <Upload className="h-4 w-4" /> Add files
              </button>
              <span className="text-xs text-gray-500">Stored as metadata (demo).</span>
            </div>
            {(ticketDraft.attachments || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {ticketDraft.attachments.map((a, idx) => (
                  <span key={idx} className="text-[11px] border border-gray-200 bg-gray-50 px-2 py-1">
                    {a.name}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-2 border-t border-gray-200 pt-3">
          <button
            type="button"
            onClick={() => setShowCreateTicket(false)}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-800 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-redhalo-red/35 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={createTicket}
            disabled={!canCreateTickets}
            className={`rh-btn-primary rounded-md px-4 py-2 text-sm hover:opacity-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-redhalo-red/40 ${!canCreateTickets ? "cursor-not-allowed opacity-50" : ""}`}
          >
            Create
          </button>
        </div>
      </ModalShell>
    </div>
  );
}

